import { clearStoredAuthSession } from "../auth/clientAuth";

/** All JSON calls use credentials so HttpOnly auth cookie is sent; never attach Bearer from JS. */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5001";
const API_FALLBACK_BASE = "https://willplay-api-h6g3bjhee5f9bpgh.centralindia-01.azurewebsites.net";
const ACTIVE_API_BASE_KEY = "willplay_active_api_base";
const FIRST_PARTY_API = "https://api.willplay.in";

function normalizeBase(base) {
  return String(base || "").trim().replace(/\/+$/, "");
}

function isLocalBase(base) {
  return /localhost|127\.0\.0\.1/i.test(normalizeBase(base));
}

function getSameOriginBase() {
  if (typeof window === "undefined") return "";
  const origin = normalizeBase(window.location.origin);
  return origin && !isLocalBase(origin) ? origin : "";
}

function isFirstPartyWillPlayApi(base) {
  try {
    const host = new URL(normalizeBase(base)).hostname.toLowerCase();
    return host === "api.willplay.in" || host.endsWith(".willplay.in");
  } catch {
    return false;
  }
}

function isStickyCookieSafeBase(base) {
  const normalized = normalizeBase(base);
  if (!normalized || isLocalBase(normalized)) return false;
  if (isFirstPartyWillPlayApi(normalized)) return true;
  return normalized === getSameOriginBase();
}

function getStoredActiveBase() {
  try {
    const v = localStorage.getItem(ACTIVE_API_BASE_KEY);
    const normalized = normalizeBase(v);
    if (!normalized) return "";
    if (!isStickyCookieSafeBase(normalized)) {
      localStorage.removeItem(ACTIVE_API_BASE_KEY);
      return "";
    }
    return normalized;
  } catch {
    return "";
  }
}

function setStoredActiveBase(base) {
  try {
    const normalized = normalizeBase(base);
    if (!normalized) return;
    // Only persist origins that keep cookie auth first-party and stable on mobile.
    if (!isStickyCookieSafeBase(normalized)) return;
    localStorage.setItem(ACTIVE_API_BASE_KEY, normalized);
  } catch {
    /* ignore */
  }
}

/** True when another API base might succeed (e.g. primary DB down). */
function shouldFailoverToNextBase(status) {
  return status === 500 || status === 502 || status === 503 || status === 504;
}

/** Persist host only after a non–5xx response so we do not stick a broken deployment. */
function shouldPersistApiBaseForStatus(status) {
  return status < 500;
}

/**
 * Base URL for raw fetch/SignalR so it matches the host used by apiJson (sticky first-party).
 * @returns {string}
 */
export function getPreferredApiBase() {
  const sameOrigin = getSameOriginBase();
  if (sameOrigin) return sameOrigin;
  const stored = getStoredActiveBase();
  if (stored) return stored;
  const env = normalizeBase(API_BASE_URL);
  if (env && !isLocalBase(env)) return env;
  if (typeof process !== "undefined" && process.env.NODE_ENV === "production") {
    return FIRST_PARTY_API;
  }
  return env || FIRST_PARTY_API;
}

/** Absolute URL for an API path (e.g. "/api/Auth/login"). */
export function buildApiUrl(path) {
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${getPreferredApiBase().replace(/\/+$/, "")}${rel}`;
}

function resolveApiBases(fastAuth = false) {
  const sameOrigin = getSameOriginBase();
  const preferred = getStoredActiveBase();
  const env = normalizeBase(API_BASE_URL);

  if (fastAuth) {
    const out = [];
    const seen = new Set();
    for (const item of [sameOrigin, preferred, env]) {
      const v = normalizeBase(item);
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out.length > 0 ? out : [normalizeBase(API_BASE_URL) || FIRST_PARTY_API];
  }

  const raw = [sameOrigin, preferred, env, FIRST_PARTY_API, API_FALLBACK_BASE];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const v = normalizeBase(item);
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

if (typeof process !== "undefined" && process.env.NODE_ENV === "production") {
  const base = normalizeBase(API_BASE_URL);

  if (/localhost|127\.0\.0\.1/i.test(base)) {
    throw new Error(
      "[WillPlay] Production build still points at localhost. Set REACT_APP_API_BASE_URL to your live API (e.g. https://api.willplay.in) before npm run build."
    );
  }

  if (base.startsWith("http://")) {
    console.warn(
      "[WillPlay] REACT_APP_API_BASE_URL uses http:// in production. Prefer https:// for cookies and security."
    );
  }
}

/** @typedef {{ response: Response, data: unknown }} ApiJsonResult */

export async function apiJson(path, opts = {}) {
  const {
    method = "GET",
    body,
    rawBody,
    query,
    auth = true,
    credentials: credentialsOverride,
    expireSessionOn401 = false,
    fastAuth = false
  } = opts;

  const rel = path.startsWith("/") ? path : `/${path}`;

  const buildUrl = (base) => {
    const url = new URL(rel, `${normalizeBase(base)}/`);
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        url.searchParams.set(k, String(v));
      });
    }
    return url.toString();
  };

  const headers = {
    Accept: "application/json",
    "X-WillPlay-Request": "1"
  };

  const isGet = method === "GET" || method === "HEAD";

  if (rawBody !== undefined) {
    headers["Content-Type"] = "application/json";
  } else if (!isGet && body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const init = {
    method,
    headers,
    credentials: credentialsOverride ?? "include",
    body:
      rawBody !== undefined
        ? rawBody
        : !isGet && body !== undefined
          ? JSON.stringify(body)
          : undefined
  };

  let response = null;
  let lastError = null;
  const bases = resolveApiBases(fastAuth);
  const attemptTimeoutMs = fastAuth ? 12_000 : 28_000;

  for (let i = 0; i < bases.length; i++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), attemptTimeoutMs);
    try {
      response = await fetch(buildUrl(bases[i]), { ...init, signal: controller.signal });
      const status = response.status;
      const canRetry =
        shouldFailoverToNextBase(status) &&
        i < bases.length - 1 &&
        (!fastAuth || i === 0);
      if (canRetry) {
        continue;
      }
      if (shouldPersistApiBaseForStatus(status)) {
        setStoredActiveBase(bases[i]);
      }
      break;
    } catch (err) {
      lastError = err;
      if (i === bases.length - 1) throw err;
    } finally {
      clearTimeout(timer);
    }
  }
  if (!response) {
    throw lastError || new Error("Could not reach API.");
  }

  if (response.status === 401 && (auth || expireSessionOn401)) {
    clearStoredAuthSession();
  }

  const text = await response.text();

  let data = null;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  return { response, data };
}

export { API_BASE_URL };